import { pickT } from "@repo/shared";
import { useTheme } from "next-themes";
import { useEffect, useMemo, useState } from "react";
import { VegaEmbed } from "react-vega";
import { useContainerWidth } from "@/lib/hooks/use-container-width";
import { useUserLanguage } from "@/lib/i18n/context";
import type { MultiLineData } from "@/lib/utils/vega-chart-shared";
import { T } from "./multi-line-chart.i18n";
import { createMultiLineChartSpec } from "./vega-specs";

type MultiLineChartProps = {
  data: MultiLineData[];
  width?: number | "container";
  height?: number;
  title?: string;
  line1Label?: string;
  line2Label?: string;
  yAxisLabel?: string;
  primaryColor?: string;
  lang?: string;
};

/**
 * Client-side multi-line chart component using Vega-Lite
 *
 * Displays two lines for comparing values (e.g., with/without cost).
 * Uses the same chart specification as server-side rendering,
 * ensuring consistent appearance in UI and PDFs.
 */
export function MultiLineChart({
  data,
  width = "container",
  height = 300,
  title,
  line1Label = "Without Cost",
  line2Label = "With Cost",
  yAxisLabel,
  primaryColor,
  lang,
}: MultiLineChartProps) {
  const { userLanguage } = useUserLanguage();
  // One language for the labels and the Vega tooltips.
  const chartLang = lang ?? userLanguage;
  const t = pickT(T, chartLang);
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  // When width is "container", measure the wrapper and render at that pixel
  // width so the chart fills its column / full-width slot (see useContainerWidth).
  const [containerRef, containerWidth] = useContainerWidth<HTMLDivElement>();
  const effectiveWidth = typeof width === "number" ? width : containerWidth;

  useEffect(() => {
    setMounted(true);
  }, []);

  const isDark = mounted && resolvedTheme === "dark";

  // Generate spec on client side and ensure it's serializable
  const spec = useMemo(() => {
    if (!data || data.length === 0 || effectiveWidth === null) {
      return null;
    }

    const chartSpec = createMultiLineChartSpec(data, {
      width: effectiveWidth,
      height,
      title,
      line1Label,
      line2Label,
      yAxisLabel,
      isDark,
      primaryColor,
      lang: chartLang,
    });

    // Ensure the spec is JSON-serializable by parsing/stringifying
    return JSON.parse(JSON.stringify(chartSpec));
  }, [
    data,
    effectiveWidth,
    height,
    title,
    line1Label,
    line2Label,
    yAxisLabel,
    isDark,
    primaryColor,
    chartLang,
  ]);

  if (!data || data.length === 0) {
    return (
      <div className="w-full p-6 text-center">
        <p className="text-sm text-muted-foreground">{t.noData}</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="w-full [&_svg]:max-w-full [&_svg]:h-auto"
      style={{ minHeight: height }}
    >
      {spec && (
        <VegaEmbed
          spec={spec}
          options={{
            actions: false,
            renderer: "svg",
          }}
        />
      )}
    </div>
  );
}
